import BackHeader from '@/layout/components/BackHeader'
import { DEFAULT_ROUTE, IMG_PLACE_HOLDER } from '@/constant/common'
import { ArrowLeftOutlined, TagOutlined } from '@ant-design/icons'
import { Button, Empty, Image, message, Popover, Tabs } from 'antd'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import './ProductDetailPage.style.less'
import { useQuery } from 'react-query'
import {
  getProductDetail,
  deleteProduct,
  ProductDefaultFieldSetting,
  ProductDetail,
  ProductDetailPropertyValue,
  ProductFieldSetting,
  ProductMedia,
} from '../ProductTransform'
import LoadingComponent from '@/components/LoadingComponent/LoadingComponent'
import Header from '@/layout/components/Header'
import { useMobileSidebar } from '@/layout/components/MobileSidebarContext'
import { useEffect, useRef, useState } from 'react'
import ProductDeleteModal from '../components/ProductDeleteModal/ProductDeleteModal'
import { useAuth } from '@/providers/auth.provider'
import {
  isProductCreatedBySupplier,
  useProductPermission,
} from '../ProductPermission'
import { USER_ROLE } from '@/constant/user'
import { orderBy } from 'lodash'
import { useProductFieldPermission } from '../ProductFieldPermission'
import type {
  ProductFieldAccess,
  ProductFieldPermissionTarget,
} from '../ProductFieldPermission.types'
import ProductActionMenu from '../components/ProductActionMenu/ProductActionMenu'
import { useFreshProductSetting } from '../ProductSettingQuery'
import { getProductRedirectErrorMessage } from '../ProductError'

type ProductDetailItem = {
  key: string
  label: string
  data_type: string
  value?: string | number | null
  image?: ProductMedia | null
  files?: ProductMedia[] | null
  descriptionValue?: string | number | null
}

const NATIVE_PRODUCT_DETAIL_FIELDS = [
  'factory',
  'name',
  'number',
  'picture',
  'supplier',
]

function getFieldDisplayName(
  fieldNames: ProductFieldSetting[] = [],
  field: string,
  fallbackName: string
) {
  const matchedField = fieldNames.find((fieldName) => {
    return fieldName.field === field
  })

  return matchedField?.display_name || fallbackName
}

function parseJsonValue(value?: unknown, depth = 0): unknown {
  if (!value) return null
  if (typeof value !== 'string') return value

  try {
    const parsedValue = JSON.parse(value)

    if (typeof parsedValue === 'string' && depth < 3) {
      return parseJsonValue(parsedValue, depth + 1)
    }

    return parsedValue
  } catch {
    const unescapedValue = value.replace(/\\"/g, '"')
    const mediaObjectMatch = unescapedValue.match(
      /\{[^{}]*(media_id|url_path|thumbnail_url_path)[^{}]*\}/
    )

    if (mediaObjectMatch?.[0] && mediaObjectMatch[0] !== value) {
      return parseJsonValue(mediaObjectMatch[0], depth + 1)
    }

    return value
  }
}

function parseProductMediaValue(
  value?: string | ProductMedia | Record<string, unknown> | null
): ProductMedia | null {
  const parsedValue = parseJsonValue(value)

  if (Array.isArray(parsedValue)) {
    return (parsedValue[0] as ProductMedia | undefined) ?? null
  }

  if (parsedValue && typeof parsedValue === 'object') {
    const objectValue = parsedValue as Record<string, unknown>

    if (!Object.keys(objectValue).length) {
      return null
    }

    if (objectValue.url_path || objectValue.thumbnail_url_path) {
      return objectValue as ProductMedia
    }

    const firstKey = Object.keys(objectValue)[0]
    const nestedValue = parseJsonValue(firstKey)

    if (
      nestedValue &&
      typeof nestedValue === 'object' &&
      Object.keys(nestedValue).length
    ) {
      return nestedValue as ProductMedia
    }
  }

  return null
}

function parseProductMediaListValue(
  value?:
    | string
    | ProductMedia
    | ProductMedia[]
    | Record<string, unknown>
    | null
): ProductMedia[] {
  const parsedValue = parseJsonValue(value)

  if (Array.isArray(parsedValue)) {
    return parsedValue.filter(
      (media) => media && typeof media === 'object' && Object.keys(media).length
    ) as ProductMedia[]
  }

  if (parsedValue && typeof parsedValue === 'object') {
    const objectValue = parsedValue as Record<string, unknown>

    return Object.keys(objectValue).length ? [objectValue as ProductMedia] : []
  }

  return []
}

function getProductHeroImage(product?: ProductDetail | null) {
  if (
    product?.picture &&
    typeof product.picture === 'object' &&
    product.picture.url_path
  ) {
    return product.picture.thumbnail_url_path || product.picture.url_path
  }

  const productImage = product?.property_values?.find(
    (propertyValue) =>
      propertyValue.field === 'productImage' && propertyValue.value
  )
  const image = parseProductMediaValue(productImage?.value)

  return image?.thumbnail_url_path || image?.url_path || IMG_PLACE_HOLDER
}

function getProductHeroPreviewImage(product?: ProductDetail | null) {
  if (
    product?.picture &&
    typeof product.picture === 'object' &&
    product.picture.url_path
  ) {
    return product.picture.url_path
  }

  const productImage = product?.property_values?.find(
    (propertyValue) =>
      propertyValue.field === 'productImage' && propertyValue.value
  )
  const image = parseProductMediaValue(productImage?.value)

  return image?.url_path || getProductHeroImage(product)
}

function getTextValue(propertyValue: ProductDetailPropertyValue) {
  const parsedValue = parseJsonValue(propertyValue.value)

  if (
    typeof parsedValue === 'string' ||
    typeof parsedValue === 'number' ||
    typeof parsedValue === 'boolean'
  ) {
    return String(parsedValue)
  }

  if (parsedValue && typeof parsedValue === 'object') {
    return '-'
  }

  return typeof propertyValue.value === 'string'
    ? propertyValue.value || '-'
    : '-'
}

function buildSystemProductDetailItem(
  product: ProductDetail,
  field: ProductDefaultFieldSetting,
  fieldNames: ProductFieldSetting[]
): ProductDetailItem {
  const label = getFieldDisplayName(fieldNames, field.field, field.name)

  if (field.field === 'supplier') {
    return {
      key: field.field,
      data_type: field.data_type,
      label,
      value: product.supplier?.name || '-',
    }
  }

  if (field.field === 'factory') {
    return {
      key: field.field,
      data_type: field.data_type,
      label,
      value: product.factory?.name || '-',
    }
  }

  if (field.field === 'number') {
    return {
      key: field.field,
      data_type: field.data_type,
      label,
      value: product.number || '-',
    }
  }

  if (field.field === 'name') {
    return {
      key: field.field,
      data_type: field.data_type,
      label,
      value: product.name || '-',
    }
  }

  if (field.field === 'picture') {
    return {
      key: field.field,
      data_type: field.data_type,
      label,
      image:
        product.picture && typeof product.picture === 'object'
          ? product.picture
          : null,
    }
  }

  return {
    key: field.field,
    data_type: field.data_type || '',
    label,
    value: '-',
  }
}

export function buildProductDetailItems(
  product: ProductDetail | null | undefined,
  fields: ProductDefaultFieldSetting[],
  fieldNames: ProductFieldSetting[],
  getFieldAccess: (target: ProductFieldPermissionTarget) => ProductFieldAccess
) {
  if (!product) return []

  const propertyValueMap = new Map(
    product.property_values?.map((propertyValue) => [
      propertyValue.field,
      propertyValue,
    ])
  )

  return fields.flatMap((field) => {
    const label = getFieldDisplayName(fieldNames, field.field, field.name)
    const access = getFieldAccess({
      field: field.field,
      propertyId: field.property_id,
    })

    if (field.system || NATIVE_PRODUCT_DETAIL_FIELDS.includes(field.field)) {
      if (access === 'hidden') return []

      return [buildSystemProductDetailItem(product, field, fieldNames)]
    }

    const propertyValue = propertyValueMap.get(field.field)
    const isImageField =
      field.data_type === 'image' || field.data_type === 'image_description'
    const isFileField = field.data_type === 'file'
    const imageItem: ProductDetailItem = {
      key: field.field,
      data_type: field.data_type,
      label,
      value:
        isImageField || isFileField
          ? undefined
          : propertyValue
          ? getTextValue(propertyValue)
          : field.value || '-',
      image:
        isImageField && propertyValue
          ? parseProductMediaValue(propertyValue.value)
          : null,
      files:
        isFileField && propertyValue
          ? parseProductMediaListValue(propertyValue.value)
          : undefined,
    }

    if (field.data_type !== 'image_description') {
      return access === 'hidden' ? [] : [imageItem]
    }

    const descriptionAccess = field.description
      ? getFieldAccess({ field: field.description.field })
      : 'hidden'
    const items: ProductDetailItem[] = []

    if (descriptionAccess !== 'hidden') {
      items.push({
        key: field.description?.field || `${field.field}-description`,
        data_type: field.data_type,
        label: getFieldDisplayName(
          fieldNames,
          field.description?.field || '',
          field.description?.name || 'Description'
        ),
        value: propertyValue?.description?.value || '-',
        image: null,
      })
    }

    if (access !== 'hidden') {
      items.push(imageItem)
    }

    return items
  })
}

function ProductInfoItem({ item }: { item: ProductDetailItem }) {
  const imageSrc = item.image?.thumbnail_url_path || item.image?.url_path
  const previewSrc = item.image?.url_path || imageSrc
  const files = item.files?.filter((file) => file.url_path) ?? []
  const dataType = item.data_type === 'file'

  return (
    <div className="product-detail-info-item">
      <p className="product-detail-info-label">{item.label}</p>
      <div className="product-detail-info-value">
        {imageSrc ? (
          <>
            <Image
              className="product-detail-field-image"
              src={imageSrc}
              fallback={IMG_PLACE_HOLDER}
              preview={{
                src: previewSrc,
                maskClassName: 'product-detail-image-preview-mask',
              }}
            />
            {item.descriptionValue && (
              <p className="product-detail-image-description">
                {item.descriptionValue}
              </p>
            )}
          </>
        ) : dataType ? (
          <Popover
            content={
              <div className="popover-url">
                <ul>
                  {files.map((file, index) => (
                    <li
                      key={
                        file.media_id || file.id || `${item.key}-file-${index}`
                      }
                    >
                      <a
                        href={file.url_path}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {file.name || 'File'}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            }
            title="Files"
            trigger="hover"
          >
            <p className="file-url-name">Files</p>
          </Popover>
        ) : (
          <span>{item.value || '-'}</span>
        )}
      </div>
    </div>
  )
}

function ProductDetailPage() {
  const { user } = useAuth()
  const { canDeleteProduct, canEditProduct, canViewProduct } =
    useProductPermission(user)
  const {
    getFieldAccess: getConfiguredFieldAccess,
    isLoading: isLoadingFieldPermissions,
  } = useProductFieldPermission()
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { isMobile } = useMobileSidebar()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeletingProduct, setIsDeletingProduct] = useState(false)
  const hasHandledMissingProduct = useRef(false)

  const productDetailQuery = useQuery(
    ['GET_PRODUCT_DETAIL', id],
    async () => {
      if (!id) return null

      return await getProductDetail(id)
    },
    {
      enabled: Boolean(id),
      retry: false,
    }
  )
  const productSettingQuery = useFreshProductSetting()

  useEffect(() => {
    if (hasHandledMissingProduct.current) return

    const errorMessage = getProductRedirectErrorMessage(
      productDetailQuery.error
    )

    if (!errorMessage) return

    hasHandledMissingProduct.current = true
    message.error(errorMessage, 4)
    navigate(DEFAULT_ROUTE.PRODUCTS, { replace: true })
  }, [navigate, productDetailQuery.error])

  const onGoBack = () => {
    if (location.key !== 'default') {
      navigate(-1)
      return
    }

    navigate(DEFAULT_ROUTE.PRODUCTS)
  }

  if (
    productDetailQuery.isLoading ||
    productSettingQuery.isFetching ||
    isLoadingFieldPermissions
  ) {
    return <LoadingComponent />
  }

  const product = productDetailQuery.data
  const userRole = user?.subscription?.role_access?.role
  const isSupplierUser = userRole === USER_ROLE.SUPPLIER
  const isSupplierCreatedProduct = isProductCreatedBySupplier(product)
  const getFieldAccess = (target: ProductFieldPermissionTarget) =>
    getConfiguredFieldAccess({
      ...target,
      isProductCreatedBySupplier: isSupplierCreatedProduct,
      isSupplierUser,
      productSource: product?.source,
    })
  const defaultFields = orderBy(
    productSettingQuery.data?.defaultFields || [],
    'display_order'
  )
  const fieldNames = productSettingQuery.data?.fieldNames || []
  const canViewCurrentProduct = canViewProduct(product)
  const canEditCurrentProduct = canEditProduct(product)
  const canDeleteCurrentProduct = canDeleteProduct(product)
  const pictureAccess = getFieldAccess({ field: 'picture' })
  const numberAccess = getFieldAccess({ field: 'number' })
  const nameAccess = getFieldAccess({ field: 'name' })
  const visibleProductNumber =
    numberAccess !== 'hidden' ? product?.number : undefined
  const visibleProductName = nameAccess !== 'hidden' ? product?.name : undefined
  const productTitle = visibleProductNumber || visibleProductName
  const productItems = buildProductDetailItems(
    product,
    defaultFields,
    fieldNames,
    getFieldAccess
  )
  const onDeleteProduct = async () => {
    if (!id || !canDeleteProduct(product)) return

    try {
      setIsDeletingProduct(true)
      await deleteProduct(id)
      message.success('Product deleted', 3)
      navigate(DEFAULT_ROUTE.PRODUCTS)
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || 'Product cannot be deleted',
        3
      )
    } finally {
      setIsDeletingProduct(false)
    }
  }
  const actionButton = (
    <ProductActionMenu
      canDelete={canDeleteCurrentProduct}
      canEdit={canEditCurrentProduct}
      menuClassName="product-detail-action-menu-button"
      onDelete={() => setIsDeleteModalOpen(true)}
      onEdit={() => navigate(`/products/${id}/edit`)}
      triggerClassName="product-detail-action-button"
    />
  )
  const productInfoContent = (
    <div className="product-detail-info-panel">
      <h2>Product Info</h2>
      <div className="product-detail-info-divider" />
      <div className="product-detail-info-grid">
        {productItems.map((item) => (
          <ProductInfoItem item={item} key={item.key} />
        ))}
      </div>
    </div>
  )

  return (
    <>
      {isMobile ? (
        <Header
          icon={<TagOutlined className="icon" />}
          title=""
          background="linear-gradient(181.35deg, #964080 1.15%, #b1579c 98.85%)"
        />
      ) : (
        <BackHeader onClick={onGoBack} title="Back" />
      )}
      <div className="product-detail-page-container">
        {!product || !canViewCurrentProduct ? (
          <div className="product-detail-empty-container">
            <Empty description="Product not found" />
          </div>
        ) : (
          <>
            {isMobile && (
              <div className="product-detail-mobile-toolbar">
                <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={onGoBack}
                >
                  Back
                </Button>
                {actionButton}
              </div>
            )}
            <div className="product-detail-hero-panel">
              <div className="product-detail-hero-content">
                {pictureAccess !== 'hidden' && (
                  <Image
                    className="product-detail-hero-image"
                    src={getProductHeroImage(product)}
                    fallback={IMG_PLACE_HOLDER}
                    preview={{
                      src: getProductHeroPreviewImage(product),
                      maskClassName: 'product-detail-image-preview-mask',
                    }}
                  />
                )}
                {productTitle && <h1>{productTitle}</h1>}
              </div>
              {!isMobile && actionButton}
            </div>
            {isMobile ? (
              productInfoContent
            ) : (
              <Tabs
                className="product-detail-tabs"
                activeKey="product-details"
                items={[
                  {
                    key: 'product-details',
                    label: 'Product Details',
                    children: productInfoContent,
                  },
                ]}
              />
            )}
          </>
        )}
      </div>
      {isDeleteModalOpen && canDeleteCurrentProduct && (
        <ProductDeleteModal
          open={isDeleteModalOpen}
          productName={productTitle || ''}
          loading={isDeletingProduct}
          onCancel={() => setIsDeleteModalOpen(false)}
          onDelete={onDeleteProduct}
        />
      )}
    </>
  )
}

export default ProductDetailPage
